/**
 * Image abstraction over CanvasKit Image
 *
 * Provides a more natural interface for working with images while
 * maintaining compatibility with CanvasKit's underlying implementation.
 */

import { deleteImage, trackImage } from '@ck';
import { Point, point, Size, size } from '@geom';
import { canvasKit } from '@resources';
import type { Image as CKImage } from 'canvaskit';
import type { ImageSource } from './ImageSource';

/**
 * Our abstraction over CanvasKit's Image
 *
 * Provides convenient methods and properties while maintaining
 * access to the underlying CanvasKit image for rendering operations.
 */
export class Image {
  readonly #ckImage: CKImage;
  readonly #source?: ImageSource;

  constructor(ckImage: CKImage, source?: ImageSource) {
    this.#ckImage = trackImage(ckImage);
    this.#source = source;
  }

  /**
   * Get the width of the image in pixels
   */
  get width(): number {
    return this.#ckImage.width();
  }

  /**
   * Get the height of the image in pixels
   */
  get height(): number {
    return this.#ckImage.height();
  }

  /**
   * Get the dimensions as a Size object
   */
  get size(): Size {
    return size(this.width, this.height);
  }

  /**
   * Get the center point of the image
   */
  get center(): Point {
    return point(this.width / 2, this.height / 2);
  }

  /**
   * Get the source that created this image, if available
   */
  get source(): ImageSource | undefined {
    return this.#source;
  }

  /**
   * Get the aspect ratio (width / height)
   */
  get aspectRatio(): number {
    return this.width / this.height;
  }

  /**
   * Access the underlying CanvasKit Image for direct operations
   * Use this when you need to pass the image to CanvasKit drawing functions
   */
  get ckImage(): CKImage {
    return this.#ckImage;
  }

  /**
   * Create a scaled version of this image
   */
  scale(factorOrSize: number | Size): Image {
    let targetSize: Size;

    if (typeof factorOrSize === 'number') {
      targetSize = this.size.mul(factorOrSize);
    } else {
      targetSize = factorOrSize;
    }

    const surface = canvasKit.MakeSurface(targetSize.width, targetSize.height);
    if (!surface) {
      throw new Error('Failed to create surface for image scaling');
    }

    const canvas = surface.getCanvas();
    const paint = new canvasKit.Paint();

    const srcRect = canvasKit.LTRBRect(0, 0, this.width, this.height);
    const destRect = canvasKit.LTRBRect(0, 0, targetSize.width, targetSize.height);

    canvas.drawImageRect(this.#ckImage, srcRect, destRect, paint);

    const scaledCKImage = surface.makeImageSnapshot();
    surface.delete();
    paint.delete();

    return new Image(scaledCKImage);
  }

  /**
   * Encode the image to bytes in the specified format
   */
  encodeToBytes(
    format: 'png' | 'jpeg' | 'webp' = 'png',
    quality?: number
  ): Uint8Array | null {
    const ckFormat =
      format === 'png'
        ? canvasKit.ImageFormat.PNG
        : format === 'jpeg'
          ? canvasKit.ImageFormat.JPEG
          : canvasKit.ImageFormat.WEBP;

    return this.#ckImage.encodeToBytes(ckFormat, quality);
  }

  /**
   * Check if the image dimensions are valid
   */
  get isValid(): boolean {
    return this.width > 0 && this.height > 0;
  }

  /**
   * Get a string representation for debugging
   */
  toString(): string {
    return `Image(${this.width}x${this.height})`;
  }

  /**
   * Clean up the underlying CanvasKit image
   * Must be called when done with the image to prevent memory leaks
   */
  delete(): void {
    deleteImage(this.#ckImage);
  }
}
